import type { GatewayClientOptions, GatewayResult } from "./identity-client";

export type PosSalesHistoryItem = {
  line_id: string;
  item_name: string;
  variant_name: string | null;
  quantity: number;
  line_total: number;
};

export type PosSalesHistoryPayment = {
  payment_id: string;
  payment_status: string;
  payment_method_code: string;
  payment_method_name: string;
  amount: number;
  tendered_amount: number | null;
  change_amount: number | null;
  captured_at: string | null;
  settled_at: string | null;
  voided_at: string | null;
};

export type PosReceivableBusinessState = {
  receivable_id: string;
  receivable_no: string;
  receivable_status: "OPEN" | "PARTIALLY_PAID" | "PAID" | "VOIDED" | string;
  currency_code: string;
  original_amount: number;
  allocated_amount: number;
  remaining_amount: number;
  due_date: string;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  voided_at: string | null;
};

export type PosSalesHistoryOrder = {
  order_id: string;
  order_no: string;
  business_date: string;
  transaction_at: string;
  order_status: string;
  currency_code: string;
  grand_total: number;
  refunded_amount: number;
  source_type: string;
  is_test: boolean;
  performing_employee: {
    employee_id?: string;
    employee_code?: string;
    employee_name?: string;
  };
  customer: {
    customer_id?: string;
    membership_id?: string;
    customer_name?: string;
    customer_code?: string;
    membership_status?: string;
  };
  items: PosSalesHistoryItem[];
  payments: PosSalesHistoryPayment[];
  receivable?: PosReceivableBusinessState | null;
};

export type PosSalesHistoryResponse = {
  ok: true;
  contract: "GET_POS_SALES_HISTORY_V1";
  timezone: string;
  business_date: string;
  organization_id: string;
  branch_id: string;
  node_id: string;
  employee_id: string;
  permissions: {
    sales_history_visible: boolean;
    cash_void_candidate: boolean;
    credit_void_candidate?: boolean;
  };
  pagination: {
    total_count: number;
    limit: number;
    offset: number;
    returned_count: number;
  };
  orders: PosSalesHistoryOrder[];
};

export type PosOrderDetailResponse = {
  ok: true;
  contract: "GET_POS_ORDER_DETAIL_V1";
  order: {
    order_id: string;
    order_no: string;
    business_date: string;
    order_status: string;
    currency_code: string;
    subtotal_amount: number;
    modifier_amount: number;
    discount_amount: number;
    tax_amount: number;
    service_charge_amount: number;
    grand_total: number;
    refunded_amount: number;
    source_type: string;
    created_at: string;
    paid_at: string | null;
    fulfilled_at: string | null;
    voided_at: string | null;
    is_test: boolean;
    performing_employee: {
      employee_id?: string;
      employee_code?: string;
      employee_name?: string;
    };
    customer: {
      customer_id?: string;
      membership_id?: string;
      customer_name?: string;
      customer_code?: string;
      membership_status?: string;
    };
    shift_id: string | null;
  };
  lines: Array<{
    line_id: string;
    line_no: number;
    item_name: string;
    variant_name: string | null;
    quantity: number;
    unit_price: number;
    modifier_amount: number;
    discount_amount: number;
    line_total: number;
    modifiers: Array<{
      modifier_id: string;
      group_name: string;
      option_name: string;
      quantity: number;
      unit_price_delta: number;
      total_price_delta: number;
    }>;
  }>;
  payments: Array<{
    payment_id: string;
    payment_status: string;
    payment_method_code: string;
    payment_method_name: string;
    currency_code: string;
    amount: number;
    tendered_amount: number | null;
    change_amount: number | null;
    captured_at: string | null;
    settled_at: string | null;
    voided_at: string | null;
    destination_name: string | null;
  }>;
  receivable?: PosReceivableBusinessState | null;
  events: Array<{
    event_id: string;
    event_type: string;
    occurred_at: string;
    reason?: string | null;
  }>;
  capabilities: {
    can_void_cash_sale: boolean;
    void_block_reason: string | null;
    can_void_credit_sale?: boolean;
    credit_void_block_reason?: string | null;
  };
};

export class PosSalesHistoryGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: GatewayClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  list(input: {
    deviceSecret: string;
    sessionToken: string;
    businessDate?: string | null;
    search?: string | null;
    limit?: number;
    offset?: number;
  }) {
    return this.post<PosSalesHistoryResponse>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "LIST",
      business_date: input.businessDate ?? null,
      search: input.search ?? null,
      limit: input.limit ?? 50,
      offset: input.offset ?? 0,
    });
  }

  detail(input: { deviceSecret: string; sessionToken: string; orderId: string }) {
    return this.post<PosOrderDetailResponse>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "DETAIL",
      order_id: input.orderId,
    });
  }

  private async post<T>(body: Record<string, unknown>): Promise<GatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/sales-history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayResult<T>;
      if (!response.ok) return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      return payload;
    } catch (error) {
      return {
        ok: false,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
